import { action, makeObservable, observable, reaction, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { NewsSummaryEntity, NewsSummaryEntityApi, newsSummarySchema } from '@/entities/NewsSummary';
import type { NotificationsStore } from '@/stores/global/RootStore/NotificationsStore';
import call from '@/utils/api';
import { schemeParse } from '@/utils/schemeParse';

import { MetaStore } from '../../shared/MetaStore';
import { QuickThoughtsStore } from '../../shared/quickThoughts';

type OrganizationNewsPageStoreParams = {
  organizationId: number;
  activeId: number | null;
  notificationsStore: NotificationsStore;
};

type PrivateFields = '_organizationId' | '_activeId';

export class OrganizationNewsPageStore {
  private _organizationId: number;
  private _activeId: number | null;
  readonly meta = new MetaStore();
  readonly quickThoughts: QuickThoughtsStore;

  newsSummary: NewsSummaryEntity | null = null;

  constructor({ organizationId, activeId, notificationsStore }: OrganizationNewsPageStoreParams) {
    this._organizationId = organizationId;
    this._activeId = activeId;
    this.quickThoughts = new QuickThoughtsStore({
      organizationId: String(organizationId),
      notificationsStore,
    });

    makeObservable<OrganizationNewsPageStore, PrivateFields>(this, {
      newsSummary: observable,
      _organizationId: observable,
      _activeId: observable,
      fetchNewsSummary: action.bound,
    });
  }

  async fetchNewsSummary() {
    if (!this._activeId) {
      return;
    }

    this.meta.setLoadedStartMeta();

    const response = await call<NewsSummaryEntityApi>({
      endpoint: ENDPOINTS.news.summary(String(this._organizationId), String(this._activeId)),
    });

    if (response.isError) {
      this.meta.setLoadedErrorMeta();

      return;
    }

    runInAction(() => {
      const parsed = schemeParse(newsSummarySchema, response.data);

      if (parsed.success) {
        this.newsSummary = parsed.data;
      }

      this.meta.setLoadedSuccessMeta();
    });
  }

  setActiveId = (activeId: number | null) => {
    this._activeId = activeId;
  };

  private _onChangeActiveId = reaction(
    () => this._activeId,
    () => {
      this.fetchNewsSummary();
    },
    { fireImmediately: true },
  );

  destroy() {
    this.meta.destroy();
    this.quickThoughts.destroy();
    this._onChangeActiveId();
  }
}
